import { Injectable } from "@angular/core";
import { map, Observable } from "rxjs";

import { ToastType } from "../constants/toast-type";
import { DeductionsModel, DirectDepositModel, PayrollAddressModel, PTOModel, AnnualStatementsModel, StatementModel, AnnualStatementEmailCriteria, PayStatementEmailCriteria } from "../models/payroll";
import { ApiService } from "./api.service";
import { ToastService } from "./toast.service";
import { ApiHeaderType } from "../constants/api-header-type";
import { FederalTax, MemberStateTaxes, StateTaxExtended } from "../models/taxes";
import { PayStatementListItem, PayStatementModel, PayStatementPayload } from "../models/pay-statement";
import { ChangeRequestAddressModel, ChangeRequestFederalTaxModel, ChangeRequestStateTax } from "../models/change-requests";
import { PayrollDashboardModel } from "../models/payroll-dashboard-model";

const controllerRoute = '/payroll';

@Injectable()
export class PayrollService {

    constructor(
        private apiService: ApiService,
        private toastService: ToastService,
    ) { }

    public getAddress(): Observable<PayrollAddressModel> {
        return this.apiService.get(`${controllerRoute}/address`).pipe(
            map((response: PayrollAddressModel) => {
                return response;
            })
        );
    }

    public verifyChangeAdress(payload: ChangeRequestAddressModel): Observable<ChangeRequestAddressModel> {
        return this.apiService.post(`${controllerRoute}/verify-address-change-request`, payload).pipe(
            map((response: ChangeRequestAddressModel) => {
                return response;
            })
        );
    }

    public saveAdressChange(payload: ChangeRequestAddressModel): Observable<ChangeRequestAddressModel> {
        return this.apiService.post(`${controllerRoute}/save-address-change-request`, payload).pipe(
            map((response: ChangeRequestAddressModel) => {
                if (response.errorMessage)
                    this.toastService.buildToastMessage(ToastType.error, "Save Error", "Address changes are not saved!");
                else
                    this.toastService.buildToastMessage(ToastType.success, "Address Saved", "Address changes saved!");
                return response;
            })
        );
    }

    public getDeductions(): Observable<DeductionsModel> {
        return this.apiService.get(`${controllerRoute}/deductions`).pipe(
            map((response: DeductionsModel) => {
                return response;
            })
        );
    }

    public getPTO(): Observable<PTOModel> {
        return this.apiService.get(`${controllerRoute}/pto`).pipe(
            map((response: PTOModel) => {
                return response;
            })
        );
    }

    public getCurrentDirectDeposit(): Observable<DirectDepositModel> {
        return this.apiService.get(`${controllerRoute}/current-direct-deposit`).pipe(
            map((response: DirectDepositModel) => {
                return response;
            })
        );
    }

    public saveDirectDeposit(payload: DirectDepositModel): Observable<DirectDepositModel> {
        return this.apiService.post(`${controllerRoute}/save-direct-deposit`, payload).pipe(
            map((response: DirectDepositModel) => {
                return response;
            })
        );
    }

    public getPayStatements(): Observable<Array<PayStatementListItem>> {
        return this.apiService.get(`${controllerRoute}/pay-statements`).pipe(
            map((response: Array<PayStatementListItem>) => {
                return response;
            })
        );
    }

    public getPayStatement(payload: PayStatementPayload): Observable<PayStatementModel> {
        return this.apiService.post(`${controllerRoute}/pay-statement`, payload).pipe(
            map((response: PayStatementModel) => {
                return response;
            })
        );
    }

    public getAnnualStatements(): Observable<Array<AnnualStatementsModel>> {
        return this.apiService.get(`${controllerRoute}/annual-statements`).pipe(
            map((response: Array<AnnualStatementsModel>) => {
                return response;
            })
        );
    }

    public downloadAnnualStatementDocument(payload: StatementModel): Observable<Blob> {
        return this.apiService.post(`${controllerRoute}/annual-statement-document`, payload, ApiHeaderType.blob).pipe(
            map((response: Blob) => {
                return response;
            })
        );
    }

    public downloadPayStatementReport(payload: StatementModel): Observable<Blob> {
        return this.apiService.post(`${controllerRoute}/pay-statement-report`, payload, ApiHeaderType.blob).pipe(
            map((response: Blob) => {
                return response;
            })
        );
    }

    public getFederalTaxes(): Observable<FederalTax> {
        return this.apiService.get(`${controllerRoute}/federal-taxes`).pipe(
            map((response: FederalTax) => {
                return response;
            }));
    }

    public saveFederalTaxChange(payload: ChangeRequestFederalTaxModel): Observable<ChangeRequestFederalTaxModel> {
        return this.apiService.post(`${controllerRoute}/save-federal-tax-change`, payload).pipe(
            map((response: ChangeRequestFederalTaxModel) => {
                this.toastService.buildToastMessage(ToastType.success, "Federal Tax Change Saved", "Federal tax change request saved!");
                return response;
            }));
    }

    public getStateTaxes(): Observable<MemberStateTaxes> {
        return this.apiService.get(`${controllerRoute}/state-taxes`).pipe(
            map((response: MemberStateTaxes) => {
                return response;
            }));
    }

    public saveStateTaxChange(payload: ChangeRequestStateTax): Observable<ChangeRequestStateTax> {
        return this.apiService.post(`${controllerRoute}/save-state-tax-change`, payload).pipe(
            map((response: ChangeRequestStateTax) => {
                this.toastService.buildToastMessage(ToastType.success, "State Tax Change Saved", "State tax change request saved!");
                return response;
            }));
    }

    public sendAnnualStatementToEmail(payload: AnnualStatementEmailCriteria): Observable<null> {
        return this.apiService.post(`${controllerRoute}/annual-statement-email`, payload).pipe(
            map((response: null) => {
                this.toastService.buildToastMessage(ToastType.success, "Success!", "Annual Statement sent!");
                return response;
            }));
    }

    public sendPayStatementToEmail(payload: PayStatementEmailCriteria): Observable<null> {
        return this.apiService.post(`${controllerRoute}/pay-statement-email`, payload).pipe(
            map((response: null) => {
                this.toastService.buildToastMessage(ToastType.success, "Success!", "Pay Statement sent!");
                return response;
            }));
    }


    public updateStateForm(payload: StateTaxExtended): Observable<StateTaxExtended> {
        return this.apiService.post(`${controllerRoute}/update-state-tax-form`, payload).pipe(map((response: StateTaxExtended) => {
            return response;
        }));
    }

    public getPayrollDashboard(): Observable<PayrollDashboardModel> {
        return this.apiService.get(`${controllerRoute}/dashboard`).pipe(
            map((response: PayrollDashboardModel) => {
                return response;
            }),
        );
    }

}